import { hasValidCoords, type LatLng } from './distance'

/** Successful lookups persist in localStorage; failures are remembered for this session only. */
export type GeocodeEntry =
  | { status: 'ok'; lat: number; lng: number; at: number }
  | { status: 'failed'; at: number; error?: string }

const STORAGE_PREFIX = 'rt-geocode:v1:'
const memory = new Map<string, GeocodeEntry>()

export function normalizeAddressKey(address: string | null | undefined): string {
  return String(address ?? '').trim().toLowerCase().replace(/\s+/g, ' ').replace(/\s*,\s*/g, ', ')
}

function readStorage(key: string): GeocodeEntry | null {
  try {
    const raw = window.localStorage.getItem(STORAGE_PREFIX + key)
    if (!raw) return null
    const parsed = JSON.parse(raw) as GeocodeEntry
    return parsed?.status === 'ok' && hasValidCoords(parsed) ? parsed : null
  } catch {
    return null
  }
}

export function readGeocodeCache(address: string | null | undefined): GeocodeEntry | null {
  const key = normalizeAddressKey(address)
  if (!key) return null
  const cached = memory.get(key)
  if (cached) return cached
  const stored = readStorage(key)
  if (stored) memory.set(key, stored)
  return stored
}

export function writeGeocodeCache(address: string, entry: GeocodeEntry) {
  const key = normalizeAddressKey(address)
  if (!key) return
  memory.set(key, entry)
  if (entry.status !== 'ok') return
  try {
    window.localStorage.setItem(STORAGE_PREFIX + key, JSON.stringify(entry))
  } catch {
    // Storage full or blocked: the in-memory entry still serves this session.
  }
}

/** Fills missing job coordinates from previously geocoded addresses (survives polling/reloads). */
export function applyGeocodeCache<T extends { lat: number | null; lng: number | null; address?: string | null }>(item: T): T {
  if (hasValidCoords(item) || !item.address) return item
  const cached = readGeocodeCache(item.address)
  return cached?.status === 'ok' ? { ...item, lat: cached.lat, lng: cached.lng } : item
}

export function cachedCoords(address: string | null | undefined): LatLng | null {
  const cached = readGeocodeCache(address)
  return cached?.status === 'ok' ? { lat: cached.lat, lng: cached.lng } : null
}

/** Test helper. */
export function resetGeocodeMemoryCache() {
  memory.clear()
}
